import { styled, CSSObject, css } from '@withjoy/joykit/theme';
import { Box, BoxProps } from '../Box';

import { CardContentSize, CardContentAlignment, CardMediaLayer, CardMediaBackgroundPlacement } from './Card.types';
import { CARD_MEDIA_BACKGROUND_PLACEMENT } from './Card.constants';
import { animationTransition } from '@shared/utils/animationTransition';

// ========================
// Card Content
// ========================

const contentAlignmentRules: ReadonlyRecord<CardContentAlignment, Partial<{ marginLeft: string; marginRight: string }>> = {
  left: {},
  right: {
    marginLeft: 'auto',
  },
  center: {
    marginLeft: 'auto',
    marginRight: 'auto',
  },
};

const CARD_CONTENT_SIZE = {
  full: {
    width: '100%',
  },
  half: {
    width: '54%',
  },
  custom: (percentage: number) => ({ width: `${Math.min(Math.max(percentage, 0), 1) * 100}%` }),
} as const;

export const StyledCardContentRoot = styled(Box)<{
  $align: CardContentAlignment;
  $size: CardContentSize | number;
}>`
  ${({ $align, $size }) => {
    const contentSize = typeof $size === 'number' ? CARD_CONTENT_SIZE.custom($size) : CARD_CONTENT_SIZE[$size];
    return {
      '& > div': {
        ...contentSize,
        ...contentAlignmentRules[$align],
      },
    };
  }}
  position: relative;
  grid-area: content;
`;

// ========================
// Card Actions
// ========================

export const getCardActionsStyles = (args: { elevation: 0 | 1 }): ReadonlyRecord<'Root', BoxProps> => {
  const { elevation } = args;
  return {
    Root: {
      alignItems: 'center',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      _groupHover:
        elevation === 1
          ? {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
            }
          : {},
      display: 'flex',
      gridArea: 'actions',
      justifyContent: 'center',
      padding: 5,
      position: 'relative',
      transition: animationTransition('background-color'),
    },
  };
};

export const StyledCardActionsRoot = styled(Box)`
  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

// ========================
// Card Media
// ========================

export const getCardMediaStyles = (args: {
  src: string;
  layer: CardMediaLayer;
  placement: CardMediaBackgroundPlacement;
  foregroundHeight: string | number | undefined;
}): ReadonlyRecord<'Root', BoxProps> => {
  const { src, layer, placement, foregroundHeight } = args;
  return {
    Root: {
      backgroundImage: `url(${src})`,
      top: '0px',
      bottom: '0px',
      left: '0px',
      right: '0px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: CARD_MEDIA_BACKGROUND_PLACEMENT[placement],
      backgroundSize: placement === 'center' ? 'cover' : 'contain',
      transformOrigin: placement,
      ...(layer === 'background'
        ? {
            position: 'absolute',
            height: '100%',
          }
        : {
            position: 'relative',
            height: foregroundHeight,
          }),
    },
  };
};

export const StyledCardMediaWrapper = styled(Box)<{ $enableWindowPeek: boolean; $windowHeight: string | number }>`
  grid-area: media;
  ${({ $enableWindowPeek, $windowHeight }) => {
    return $enableWindowPeek
      ? {
          '&::before': {
            height: $windowHeight,
            width: '100%',
            display: 'block',
            content: '" "',
          },
        }
      : {};
  }}
`;

export const StyledCardMediaRoot = styled(Box)``;

// ========================
// Card
// ========================

const cardGridRules = css`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    'media'
    'content'
    'actions';
`;

export const getCardStyles = (args: { backgroundColor: string; isInteractive: boolean }): ReadonlyRecord<'Root', BoxProps> => {
  const { backgroundColor, isInteractive } = args;
  return {
    Root: {
      backgroundColor: backgroundColor,
      borderRadius: '8px',
      cursor: isInteractive ? 'pointer' : 'default',
      maxWidth: '100%',
      overflow: 'hidden',
      position: 'relative',
      width: '328px',
    },
  };
};

export const StyledCardRoot = styled(Box)<{ $applyGridRules: boolean; $style: CSSObject }>`
  ${({ $applyGridRules }) => ($applyGridRules ? cardGridRules : undefined)}
  ${({ $style }) => {
    return $style;
  }}
`;
